import React, { useEffect, useEffectEvent, useRef, useState } from "react";
import {
    Button,
    Card,
    ConfigProvider,
    Input,
    Space,
    Table,
    App,
    Tag,
    Tooltip,
    TableProps,
    Typography,
    theme,
} from "antd";
import {
    MinusCircleOutlined,
    PlusOutlined,
    MinusOutlined,
    SearchOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next"; // Import the translation hook
import type {
    FilterDropdownProps,
    SorterResult,
} from "antd/es/table/interface";
import AddCodeModal from "./AddCodeModal";
import { CodeCreate, CodeType } from "@openapi";
import useLocale from "@/hooks/useLocale";
import type { CodeListFilter, CodeRow } from "@/hooks/useCodeListWindow";

interface CodeTableBaseProps<Row extends CodeRow> {
    icon: React.ReactNode;
    /** The rows loaded so far, in the order to show them. */
    rows: Row[];
    /** How many codes match the filter, loaded or not. */
    total: number;
    hasMore: boolean;
    /** Loads the next page; called as the list scrolls near its end. */
    onLoadMore: () => void;
    /** The Name search, the Type choice and the Last used order. */
    filter: CodeListFilter;
    onFilterChange: (filter: CodeListFilter) => void;
    loading: boolean;
    handleSave: (newCode: CodeCreate) => Promise<void>;
    handleDelete: (id: string) => Promise<void>;
    getEditorUrl: (type: CodeType, id: string) => string;
    isLocal: boolean;
}

// How close to the bottom of the list (px) the next page is loaded.
const LOAD_MORE_MARGIN_PX = 200;

// Every code type, as the tags show them ("python turtle"): one page of
// rows cannot tell which types the user has.
const TYPE_FILTERS = Object.values(CodeType)
    .sort()
    .map((type) => ({ text: type.replace(/_/g, " "), value: type }));

// A list of codes, local or cloud, that loads more rows as it scrolls. The
// column filters and the sort are reported to the owner, which fetches or
// filters the rows accordingly.
function CodeTableBase<Row extends CodeRow>({
    icon,
    rows,
    total,
    hasMore,
    onLoadMore,
    filter,
    onFilterChange,
    loading,
    handleSave,
    handleDelete,
    getEditorUrl,
    isLocal,
}: CodeTableBaseProps<Row>) {
    const { t } = useTranslation(); // Initialize the translation function
    const { modal } = App.useApp();
    const { token } = theme.useToken();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [showActionColumn, setShowActionColumn] = useState(false);
    const locale = useLocale();
    const { term = "", types = [], order = "descend" } = filter;

    // The table body (scroll.y makes it the scrolling element) loads the
    // next page near its end, and keeps loading while a page does not even
    // fill it, since an unfilled body cannot be scrolled.
    const tableRef = useRef<HTMLDivElement>(null);
    const loadMoreNearEnd = useEffectEvent((body: HTMLElement) => {
        if (!hasMore || body.clientHeight === 0) return;
        const nearEnd =
            body.scrollTop + body.clientHeight >=
            body.scrollHeight - LOAD_MORE_MARGIN_PX;
        if (nearEnd) onLoadMore();
    });
    useEffect(() => {
        const body =
            tableRef.current?.querySelector<HTMLElement>(".ant-table-body");
        if (!body) return;
        const onScroll = () => loadMoreNearEnd(body);
        body.addEventListener("scroll", onScroll);
        loadMoreNearEnd(body);
        return () => body.removeEventListener("scroll", onScroll);
    }, [rows.length, hasMore]);

    // antd's customized filter panel: a search box in the Name column header
    const nameSearch = ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
    }: FilterDropdownProps) => (
        <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
            <Input
                autoFocus
                placeholder={t("codeTable.search")}
                aria-label={t("codeTable.search")}
                value={(selectedKeys[0] as string | undefined) ?? ""}
                onChange={(e) =>
                    setSelectedKeys(e.target.value ? [e.target.value] : [])
                }
                onPressEnter={() => confirm()}
                style={{ marginBottom: 8, display: "block" }}
            />
            <Space>
                <Button
                    type="primary"
                    size="small"
                    icon={<SearchOutlined />}
                    onClick={() => confirm()}
                    style={{ width: 90 }}
                >
                    {t("codeTable.searchButton")}
                </Button>
                <Button
                    size="small"
                    onClick={() => {
                        clearFilters?.();
                        confirm();
                    }}
                    style={{ width: 90 }}
                >
                    {t("codeTable.reset")}
                </Button>
            </Space>
        </div>
    );

    const columns: TableProps<Row>["columns"] = [
        {
            title: t("table.column.name"), // Translated "Name"
            dataIndex: "name",
            key: "name",
            width: 120,
            filterDropdown: nameSearch,
            filterIcon: (filtered) => (
                <SearchOutlined
                    style={{ color: filtered ? token.colorPrimary : undefined }}
                />
            ),
            // The owner filters over all codes, not only the rows shown so
            // far, so there is no onFilter here.
            filteredValue: term ? [term] : null,
            render: (text: string) => (
                <span
                    style={{
                        fontSize: "14px",
                        fontWeight: 500,
                    }}
                >
                    {text}
                </span>
            ),
        },
        {
            title: t("table.column.type"), // Translated "Type"
            width: 80,
            key: "type",
            dataIndex: "type",
            filters: TYPE_FILTERS,
            filteredValue: types.length ? types : null,
            render: (_, { type }) => (
                <>
                    {type.split("_").map((t, index, array) => {
                        let color = "green";
                        switch (t.toLowerCase()) {
                            case "logo":
                                color = "green";
                                break;
                            case "cpp":
                                color = "blue";
                                break;
                            case "python":
                                color = "purple";
                                break;
                            case "blockly":
                                color = "cyan";
                                break;
                            case "pybricks":
                                color = "orange";
                                break;
                            case "soldered":
                                color = "#722ed1";
                                break;
                            default:
                                color = "gray";
                                break;
                        }
                        return (
                            <Tag
                                color={color}
                                key={t}
                                style={{
                                    display: "block",
                                    marginBottom:
                                        index === array.length - 1 ? 0 : 4,
                                    textAlign: "center",
                                }}
                            >
                                {t.toLowerCase()}
                            </Tag>
                        );
                    })}
                </>
            ),
        },
        {
            title: t("table.column.code"), // Translated "Code"
            dataIndex: "code",
            key: "code",
            responsive: ["lg" as const],
            ellipsis: {
                showTitle: false,
            },
            // The cloud list carries only the start of each program
            render: (code: string) => {
                return (
                    <Tooltip
                        placement="topLeft"
                        title={
                            <pre
                                style={{
                                    margin: 0,
                                    whiteSpace: "pre-wrap",
                                    maxWidth: 400,
                                    maxHeight: 300,
                                    overflow: "auto",
                                }}
                            >
                                {code}
                            </pre>
                        }
                    >
                        {code}
                    </Tooltip>
                );
            },
        },
        {
            title: t("table.column.createdAt"), // Translated "Created At"
            dataIndex: "created_at",
            key: "created_at",
            width: 90,
            responsive: ["xxl" as const],
            render: (text: string) =>
                new Date(text).toLocaleString("hr-HR", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: false,
                }),
        },
        {
            title: t("table.column.lastUsed"), // Translated "Last Used"
            dataIndex: "last_used",
            key: "last_used",
            width: 90,
            responsive: ["xl" as const],
            // Sorted by the owner over all matching codes, not only the
            // rows shown so far.
            sorter: true,
            sortOrder: order,
            sortDirections: ["descend", "ascend", "descend"],
            render: (text: string) =>
                text
                    ? new Date(text).toLocaleString("hr-HR", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: false,
                      })
                    : t("table.column.never"), // Translated "Never"
        },
        ...(showActionColumn
            ? [
                  {
                      key: "action",
                      width: 40,
                      render: (_: unknown, record: Row) => (
                          <Button
                              danger
                              type="text"
                              size="small"
                              icon={<MinusCircleOutlined />}
                              onClick={() => {
                                  modal.confirm({
                                      title: t("codeTable.confirmDeletion"), // Translated "Confirm Deletion"
                                      content: t(
                                          "codeTable.confirmDeletionContent",
                                      ), // Translated "Are you sure you want to delete this code?"
                                      okText: t("codeTable.confirmDeletionOk"), // Translated "Yes"
                                      cancelText: t(
                                          "codeTable.confirmDeletionCancel",
                                      ), // Translated "No"
                                      onOk: () => handleDelete(record.id),
                                  });
                              }}
                          ></Button>
                      ),
                      onCell: () => ({
                          onClick: (e: React.MouseEvent) => {
                              e.stopPropagation();
                          },
                      }),
                  },
              ]
            : []),
    ];

    const handleTableChange: TableProps<Row>["onChange"] = (
        _pagination,
        filters,
        sorter,
    ) => {
        const single = Array.isArray(sorter) ? sorter[0] : sorter;
        const next = (single as SorterResult<Row> | undefined)?.order;
        onFilterChange({
            term: String(filters.name?.[0] ?? ""),
            types: (filters.type ?? []).map(String),
            order: next === "ascend" ? "ascend" : "descend",
        });
    };

    const filtered = term.trim() !== "" || types.length > 0;
    // "12 of 48" while more can load, the plain total once everything shows
    const countText = loading
        ? ""
        : rows.length < total
          ? t("codeTable.shown", { shown: rows.length, total })
          : String(total);

    return (
        <ConfigProvider
            theme={{
                components: {
                    Table: {
                        cellFontSizeSM: 12,
                    },
                },
            }}
        >
            <Card
                title={
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                            }}
                        >
                            {icon}
                            <span style={{ lineHeight: "24px" }}>
                                {
                                    isLocal
                                        ? t("codeTable.localStorage") // Translated "Local"
                                        : t("codeTable.cloud") // Translated "Cloud"
                                }
                            </span>
                            {countText && (
                                <Typography.Text
                                    type="secondary"
                                    style={{ fontSize: 12, fontWeight: 400 }}
                                >
                                    {countText}
                                </Typography.Text>
                            )}
                        </div>
                        <div>
                            <Button
                                type="default"
                                icon={<PlusOutlined />}
                                size="small"
                                onClick={() => setIsModalVisible(true)}
                                style={{
                                    margin: "0px 10px",
                                }}
                            >
                                {t("codeTable.create")}{" "}
                                {/* Translated "Create" */}
                            </Button>
                            <Button
                                type={showActionColumn ? "primary" : "default"}
                                danger={showActionColumn}
                                icon={<MinusOutlined />}
                                size="small"
                                onClick={() =>
                                    setShowActionColumn(!showActionColumn)
                                }
                            >
                                {t("codeTable.delete")}{" "}
                                {/* Translated "Delete" */}
                            </Button>
                        </div>
                    </div>
                }
                variant="outlined"
                style={{
                    height: "100%",
                }}
            >
                <div ref={tableRef}>
                    <Table<Row>
                        dataSource={rows}
                        columns={columns}
                        rowKey="id"
                        size="small"
                        pagination={false}
                        loading={loading}
                        onChange={handleTableChange}
                        locale={
                            filtered
                                ? { emptyText: t("codeTable.noMatch") }
                                : undefined
                        }
                        // scroll={{ y: "calc(100vh - 310px)" }} // this is terrible, but it doesn't work with 100%
                        // --home-top-strip: height of content the page puts above the tables
                        scroll={{
                            y: "calc(100vh - 230px - var(--home-top-strip, 0px))",
                        }}
                        onRow={(record) => ({
                            onClick: () => {
                                const editorUrl = getEditorUrl(
                                    record.type,
                                    record.id,
                                );
                                window.location.href = `/${locale}${editorUrl}`;
                            },
                        })}
                    />
                </div>
            </Card>
            <AddCodeModal
                isVisible={isModalVisible}
                isLocal={isLocal}
                onClose={() => setIsModalVisible(false)}
                onSave={handleSave}
            />
        </ConfigProvider>
    );
}

export default CodeTableBase;
